import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ProductShowcase = ({ products = [] }) => {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActiveIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track?.children.length) return;

    const center = track.scrollLeft + track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    Array.from(track.children).forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const distance = Math.abs(cardCenter - center);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const observer = new ResizeObserver(updateActiveIndex);
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateActiveIndex]);

  const goTo = (index) => {
    const card = trackRef.current?.children[index];
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  if (!products.length) return null;

  return (
    <section className="border-y border-[#e5e5e5] bg-[#f6f7f5] py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10 flex flex-col gap-6 md:mb-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#777777]">Product portfolio</p>
            <h2 className="text-3xl font-medium text-[#171717] md:text-5xl">Explore every product</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="mr-2 text-sm font-medium text-[#666666]">
              {String(activeIndex + 1).padStart(2, '0')} / {String(products.length).padStart(2, '0')}
            </span>
            <button
              type="button"
              aria-label="Previous product"
              onClick={() => goTo(Math.max(0, activeIndex - 1))}
              disabled={activeIndex === 0}
              className="flex h-10 w-10 items-center justify-center border border-[#b8b8b8] bg-white text-[#171717] transition-colors hover:border-[#171717] disabled:cursor-not-allowed disabled:opacity-35"
            >
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <button
              type="button"
              aria-label="Next product"
              onClick={() => goTo(Math.min(products.length - 1, activeIndex + 1))}
              disabled={activeIndex === products.length - 1}
              className="flex h-10 w-10 items-center justify-center border border-[#171717] bg-[#171717] text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-35"
            >
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          onScroll={updateActiveIndex}
          className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:gap-6"
        >
          {products.map((product, index) => (
            <article
              key={product.id}
              className="w-[84%] shrink-0 snap-start overflow-hidden border border-[#dedede] bg-white sm:w-[calc(50%-0.75rem)] lg:w-[calc((100%-3rem)/3)]"
            >
              <Link to={`/products/${product.id}`} className="group flex h-full flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] focus-visible:ring-offset-2">
                <div className="relative aspect-[4/3] overflow-hidden bg-[#e7e7e7]">
                  <img
                    src={product.image}
                    alt=""
                    loading={index < 3 ? 'eager' : 'lazy'}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
                  {product.isNew ? (
                    <span className="absolute left-4 top-4 bg-[#171717] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white">New</span>
                  ) : null}
                  <span className="absolute bottom-4 left-4 text-xs font-bold uppercase tracking-[0.16em] text-white">Product {String(index + 1).padStart(2, '0')}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="mb-3 text-2xl font-medium text-[#171717]">{product.name}</h3>
                  <p className="line-clamp-3 text-base leading-relaxed text-[#646464]">{product.shortTagline || product.summary}</p>
                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#171717]">
                    View product <SafeIcon icon={FiArrowRight} />
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </div>

        <div className="mt-4 flex gap-2" aria-label="Product carousel position">
          {products.map((product, index) => (
            <button
              key={product.id}
              type="button"
              aria-label={`Show ${product.name}`}
              aria-current={activeIndex === index ? 'true' : undefined}
              onClick={() => goTo(index)}
              className={`h-1.5 transition-all ${activeIndex === index ? 'w-8 bg-[#171717]' : 'w-3 bg-[#b8b8b8] hover:bg-[#777777]'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
